import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData, getAutoSlip } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global, ExecuteRunner } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnButton from "@shared/components/SnButton";
import SnLabel from "@shared/components/SnLabel";
import SnDateBox from "@shared/components/SnDateBox";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";

interface Props {
  Menuprops: menuInfo;
}

interface TabState {
  page: string;
  searchParams: any | null;
}

const SN_MTL_MOW_REG_009 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
    grid5: React.createRef<SnGridRef>(),
    grid6: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };
  const isFirstRender = useRef(true); // 화면 오픈시 조회 방지

  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보
  //#endregion

  // 처리 컴포넌트
  const [dateBox, setDateBoxValue] = useState<string>(dateToString(new Date()));

  const [tabPageState, setTabState] = useState<TabState>({
    page: "1",
    searchParams: null,
  });

  const dateValueChanged = async (value: string) => {
    setDateBoxValue(value);
  };

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
    grid5: [],
    grid6: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid5: [],
    grid6: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
          grid5: rtn.grid.grid5,
          grid6: rtn.grid.grid6,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (tabPageState.page) {
      if (!tabPageState.searchParams) {
        onSearch(null);
      } else {
        onSearch(tabPageState.searchParams);
      }
    }
  }, [tabPageState.page]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onExecute,
  }));
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_io: nodeData.no_io,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      // 이미 "처리" 탭이면 page 가 그대로라 [tabPageState.page] effect 가 안 돈다 → 여기서 바로 재조회
      if (tabPageState.page === "2") onSearch(searchData);
      setTabState({ page: "2", searchParams: searchData });
    }
  };
  //#endregion

  //#region onSearch
  const onSearch = async (searchData: any) => {
    try {
      if (tabPageState.page === "1") {
        const searchDataToUse = searchData || searchRef.current.getFormdata();
        const params = {
          id: "sn_mtl_mow_reg_009_01",
          params: searchDataToUse,
        };

        getGridCurrent("grid1").resetUpdate();
        getGridCurrent("grid2").resetUpdate();
        getGridCurrent("grid3").resetUpdate();
        const data: any = await fnGetDataSet(params);

        setDataSources({
          grid1: data[0],
          grid2: data[1] || [],
          grid3: data[2] || [],
          grid4: [],
          grid5: [],
          grid6: [],
        });
      } else {
        const searchDataToUse = searchData || searchRef.current.getFormdata();
        const params = {
          id: "sn_mtl_mow_reg_009_02",
          params: searchDataToUse,
        };

        getGridCurrent("grid4").resetUpdate();
        getGridCurrent("grid5").resetUpdate();
        getGridCurrent("grid6").resetUpdate();
        const data: any = await fnGetDataSet(params);

        setDataSources({
          grid1: [],
          grid2: [],
          grid3: [],
          grid4: data[0],
          grid5: data[1] || [],
          grid6: data[2] || [],
        });
      }
    } catch (error: any) {}
  };
  //#endregion

  //#region onExecute
  const onExecute = async (id: string) => {
    let hasInvalid = false;

    try {
      switch (id) {
        case "Save": // 처리
          {
            const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
            if (!focusedRowNodeH) {
              return;
            }

            if (dateBox === "") {
              await showDialog("alter", "인수일을 입력해 주세요.");
              return false;
            }

            const confirm = await showDialog("confirm", "처리하시겠습니까?");

            if (!confirm) {
              return; // 사용자가 아니오를 선택하면 함수 종료
            }

            const qtioH: any[] = [];
            const qtio: any[] = [];

            const today = dateToString(new Date());

            const insertDataH = {
              cd_firm: userInfo.cd_firm,
              no_io: "",
              cd_biz: focusedRowNodeH.data.cd_biz,
              cd_wh: focusedRowNodeH.data.cd_wh,
              cd_wh_ref: focusedRowNodeH.data.cd_wh_ref,
              dt_io: dateBox,
              cd_exch: "000",
              rt_exch: 1,
              cd_io: focusedRowNodeH.data.cd_io,
              fg_slip: "2",
              tp_slip: "5",
              fg_map: "2",
              fg_io: focusedRowNodeH.data.fg_io,
              fg_money: focusedRowNodeH.data.fg_money,
              fg_trns: "1",
              cd_dept: userInfo.cd_dept,
              cd_emp: userInfo.cd_emp,
              cd_gp: userInfo.cd_gp,
              dc_rmk: focusedRowNodeH.data.dc_rmk,
            };

            qtioH.push(insertDataH);

            getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
              const insertDataL = {
                cd_firm: userInfo.cd_firm,
                no_io: "",
                no_line: node.data.no_line,
                cd_biz: focusedRowNodeH.data.cd_biz,
                cd_wh: node.data.cd_wh,
                cd_io: focusedRowNodeH.data.cd_io,
                fg_slip: "2",
                tp_slip: "5",
                fg_io: "1",
                fg_money: "2",
                fg_map: "2",
                cd_item: node.data.cd_item,
                qt_good: node.data.qt_good,
                qt_bad: node.data.qt_bad,
                qt: node.data.qt_req_rev,
                rt_exch: 1,
                no_order: node.data.no_order,
                no_line_order: node.data.no_line_order,
                no_req: node.data.no_req,
                no_line_req: node.data.no_line,
                dc_rmk: node.data.dc_rmk,
                dt_io: dateBox,
                fg_unit: node.data.fg_unit,
                no_lot_cust: node.data.no_lot_cust,
                cd_pjt: node.data.cd_pjt,
              };
              qtio.push(insertDataL);
            });

            const cd_module: any = Menuprops.cd_module;
            const no_slip = await getAutoSlip(
              cd_module,
              11,
              today,
              Menuprops.cd_menu
            );

            qtioH[0].no_io = no_slip;

            for (let row of qtio) {
              row.no_io = no_slip;
            }

            // 요청 데이터 구성
            // lot/SN정보는 서버단에서 처리
            const SaveData = {
              qtioh: { insert: qtioH },
              qtio: { insert: qtio },
            };

            // 저장 API 호출
            const ok = await fnSave("SN_MTL_MOW_REG_009_SAVE_01", SaveData);
            if (!ok) return false;

            // 성공적으로 저장된 후 초기화
            getGridCurrent("grid1").resetUpdate();
            getGridCurrent("grid2").resetUpdate();
            getGridCurrent("grid3").resetUpdate();

            onSearch(null);
          }
          break;
        case "Delete": // 취소
          {
            const focusedRowNodeH = getGridCurrent("grid4").focusedRowNode();
            if (!focusedRowNodeH) {
              return;
            }

            getGridCurrent("grid5").forEachFilteredNode((node: RowNode) => {
              if (node.data.qt_used > 0) {
                if (!hasInvalid) {
                  hasInvalid = true;
                }
              }
            });

            if (hasInvalid) {
              await showDialog("alter", "불출된 자재의 사용이력이 존재합니다.");
              return false;
            }

            const confirm = await showDialog("confirm", "취소하시겠습니까?");

            if (!confirm) {
              return; // 사용자가 아니오를 선택하면 함수 종료
            }

            getGridCurrent("grid6").applyFilters({
              no_io: focusedRowNodeH.data.no_io,
            });

            getGridCurrent("grid6").deleteAllRows();
            getGridCurrent("grid5").deleteAllRows();
            getGridCurrent("grid4").deleteSelectedRows();

            const qtiohChanges = getGridCurrent("grid4").getChange();
            const qtioChanges = getGridCurrent("grid5").getChange();
            const qtiolotsnChanges = getGridCurrent("grid6").getChange();

            // 요청 데이터 구성
            const SaveData = {
              qtioh: qtiohChanges,
              qtio: qtioChanges,
              qtio_lotsn: qtiolotsnChanges,
            };

            // 저장 API 호출
            const ok = await fnSave("SN_MTL_MOW_REG_009_SAVE_01", SaveData);
            if (!ok) return false;

            // 성공적으로 저장된 후 초기화
            getGridCurrent("grid4").resetUpdate();
            getGridCurrent("grid5").resetUpdate();
            getGridCurrent("grid6").resetUpdate();

            setTabState({ page: "1", searchParams: null });
          }
          break;
      }
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid3").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      getGridCurrent("grid5").resetUpdate(true);
      getGridCurrent("grid6").resetUpdate(true);
    }
  };
  //#endregion

  //#region GridH2 Events
  const onFocusedRowChangedH2 = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_io = selectedRow.no_io;
      getGridCurrent("grid5").selectAndFocusRow(0);
      setSelectedDocu(no_io);
    } else {
      return;
    }
  };
  //#endregion

  //#region onTabChanged
  const onTabChanged = async (index: string) => {
    setTabState({ page: index, searchParams: null });
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onFocusedRowChanged: onFocusedRowChangedH2,
        },
      },
      grid5: {
        columns: columns.grid5,
        dataSource: dataSources.grid5,
      },
      grid6: {
        columns: columns.grid6,
        dataSource: dataSources.grid6,
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnTab
          titles={["미처리", "처리"]}
          width={"100%"}
          selectedTab={tabPageState.page}
          onTabChanged={onTabChanged}
          header={[
            <SnTabHeader
              body={[
                <div className="relative flex space-x-2">
                  <div style={{ width: 60 }}>
                    <SnLabel text="인수일" type="string" />
                  </div>
                  <div style={{ width: 180 }}>
                    <SnDateBox
                      defaultValue={dateBox}
                      onValueChanged={dateValueChanged}
                    />
                  </div>
                  <div style={{ paddingLeft: "80px" }}>
                    <SnButton
                      text={"처리"}
                      onClick={() => {
                        ExecuteRunner.run("Save");
                      }}
                    />
                  </div>
                </div>,
              ]}
            />,
            <SnTabHeader
              body={[
                <div className="relative flex space-x-2">
                  <SnButton
                    text={"취소"}
                    onClick={() => {
                      ExecuteRunner.run("Delete");
                    }}
                  />
                </div>,
              ]}
            />,
          ]}
          body={[
            <div className="flex-1 h-full min-h-0 space-y-1.5">
              <SnResizeBar direction={"height"} id="panel1" orgSize="35.5%">
                <SnGrid
                  ref={gridRefs.current["grid1"]} // Grid Name
                  columnConfig={grids.grid1.columns} // 컬럼 설정 전달
                  rowData={grids.grid1.dataSource} // Datasource
                  etcProps={{ rowSelection: "single" }}
                  filter={{
                    refs: [gridRefs.current["grid2"]],
                    columns: ["no_req"],
                  }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"height"} id="panel2" orgSize="64.5%">
                <div className="flex w-full h-full min-w-0 space-x-1.5">
                  <SnResizeBar direction={"width"} id="panel3" orgSize="60%">
                    <SnGrid
                      ref={gridRefs.current["grid2"]} // Grid Name
                      columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                      rowData={grids.grid2.dataSource} // Datasource
                      etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                      filter={{
                        refs: [gridRefs.current["grid3"]],
                        columns: ["no_req", "no_line"],
                      }}
                    />
                  </SnResizeBar>
                  <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
                    <SnGrid
                      ref={gridRefs.current["grid3"]} // Grid Name
                      columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                      rowData={grids.grid3.dataSource} // Datasource
                      etcProps={{ rowSelection: "single", title: "자재정보" }}
                    />
                  </div>
                </div>
              </SnResizeBar>
            </div>,
            <div className="flex-1 h-full min-h-0 space-y-1.5">
              <SnResizeBar direction={"height"} id="panel5" orgSize="35.5%">
                <SnGrid
                  ref={gridRefs.current["grid4"]} // Grid Name
                  columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                  rowData={grids.grid4.dataSource} // Datasource
                  etcProps={{ rowSelection: "single" }}
                  filter={{
                    refs: [gridRefs.current["grid5"]],
                    columns: ["no_io"],
                  }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"height"} id="panel6" orgSize="55.5%">
                <div className="flex w-full h-full min-w-0 space-x-1.5">
                  <SnResizeBar direction={"width"} id="panel7" orgSize="60%">
                    <SnGrid
                      ref={gridRefs.current["grid5"]} // Grid Name
                      columnConfig={grids.grid5.columns} // 컬럼 설정 전달
                      rowData={grids.grid5.dataSource} // Datasource
                      etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                      filter={{
                        refs: [gridRefs.current["grid6"]],
                        columns: ["no_io", "no_line"],
                      }}
                    />
                  </SnResizeBar>
                  <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
                    <SnGrid
                      ref={gridRefs.current["grid6"]} // Grid Name
                      columnConfig={grids.grid6.columns} // 컬럼 설정 전달
                      rowData={grids.grid6.dataSource} // Datasource
                      etcProps={{ rowSelection: "multiple", title: "자재정보" }}
                    />
                  </div>
                </div>
              </SnResizeBar>
              <SnFileUploadBox
                selectedDocu={selectedDocu}
                selectedLineDocu={0}
              />
            </div>,
          ]}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_MOW_REG_009;
